import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useHiddenPaymentMethodsStore } from './HiddenPaymentMethodStore';
import type { PaymentMethodDto } from '../../generated-api';
import { PaymentState } from '../../generated-api';
import {
  type TerminalStatusResponseLatestState as TerminalState,
  TerminalStatusResponseLatestState as TerminalStateEnum,
} from '@ordr-as/payment-service-types/epos';
import { useCallback, useMemo } from 'react';
import { terminalStatesStoreKey } from '../constants/storage-keys';
import { useLocation } from './LocationContext';
import { useQueryActivePayments } from '../hooks/useQueryActivePayments';

export type TerminalDisplayState = 'AVAILABLE' | 'BUSY' | 'OFFLINE';

type PaymentMethodActions = {
  populateStore: (paymentMethods: PaymentMethodDto[], deviceId: string) => void;
  setTerminalLatestState: (terminalId: string, latestState: TerminalState) => void;
  setTerminalLatestStates: (states: Record<string, TerminalState>) => void;
};

type PaymentMethodsStoreState = {
  // The currently‐selected location
  paymentMethods: PaymentMethodDto[];

  // Derived data
  directSales: PaymentMethodDto[];
  openBill: PaymentMethodDto[];

  // A copy of hiddenIds from useHiddenPaymentMethodsState
  hiddenSnapshot: Set<string>;
  deviceId: string | undefined;

  // The payment method matching the current device ID, if any
  deviceIdPaymentMethod: PaymentMethodDto | undefined;

  // Terminal latest states (raw API values) keyed by payment method ID
  terminalLatestStates: Record<string, TerminalState>;

  actions: PaymentMethodActions;
};

function filterPaymentMethods(paymentMethods: PaymentMethodDto[], hiddenIds: Set<string>) {
  const directSales = [];
  const openBill = [];

  for (const paymentMethod of paymentMethods) {
    if (paymentMethod.isDirectSalesMethod && !hiddenIds.has(paymentMethod.id)) {
      directSales.push(paymentMethod);
    }
    if (paymentMethod.isOpenBillMethod && !hiddenIds.has(paymentMethod.id)) {
      openBill.push(paymentMethod);
    }
  }

  return { directSales, openBill };
}

export const usePaymentMethodsStore = create<PaymentMethodsStoreState>()(
  subscribeWithSelector((set, get, api) => {
    // Re-compute derived data every time useHiddenPaymentMethodsState.hiddenIds changes
    useHiddenPaymentMethodsStore.subscribe((state) => {
      const { directSales, openBill } = filterPaymentMethods(get().paymentMethods, state.hiddenIds);

      set({
        hiddenSnapshot: state.hiddenIds,
        directSales: directSales,
        openBill: openBill,
      });
    });

    // Re-compute derived data every time location changes
    api.subscribe(
      (state) => state?.paymentMethods,
      (paymentMethods) => {
        const { directSales, openBill } = filterPaymentMethods(paymentMethods, get().hiddenSnapshot);

        set({
          directSales: directSales,
          openBill: openBill,
        });
      }
    );

    // Re-compute derived data every time location changes
    api.subscribe(
      (state) => state?.deviceId,
      (deviceId) => {
        if (deviceId === undefined) {
          set({ deviceIdPaymentMethod: undefined });
          return;
        }

        for (const paymentMethod of get().paymentMethods) {
          if (paymentMethod.id === deviceId) {
            set({ deviceIdPaymentMethod: paymentMethod });
            return;
          }
        }

        set({ deviceIdPaymentMethod: undefined });
      }
    );

    // Retrieve localStorage-stored list of hidden payment methods
    const initialHidden = useHiddenPaymentMethodsStore.getState().hiddenIds;

    // Retrieve localStorage-stored terminal states
    let initialTerminalLatestStates: Record<string, TerminalState> = {};
    try {
      const stored = localStorage.getItem(terminalStatesStoreKey);
      if (stored) {
        initialTerminalLatestStates = JSON.parse(stored);
      }
    } catch {
      // Ignore parse errors
    }

    return {
      // initial copy of hidden IDs
      hiddenSnapshot: initialHidden,

      // Selected device ID - prefer softpay app id
      deviceId: undefined,
      deviceIdPaymentMethod: undefined,

      // No location selected to start with
      paymentMethods: [],

      directSales: [],
      openBill: [],

      // Terminal latest states (raw API values)
      terminalLatestStates: initialTerminalLatestStates,

      actions: {
        populateStore: (paymentMethods, deviceId) => {
          set(() => {
            const merged: PaymentMethodDto[] = [];

            for (const pm of paymentMethods) {
              merged.push({ ...pm });
            }

            return { paymentMethods: merged, deviceId };
          });
        },
        setTerminalLatestState: (terminalId, latestState) => {
          const newStates = { ...get().terminalLatestStates, [terminalId]: latestState };
          set({ terminalLatestStates: newStates });
          try {
            localStorage.setItem(terminalStatesStoreKey, JSON.stringify(newStates));
          } catch {
            // Ignore storage errors
          }
        },
        setTerminalLatestStates: (states) => {
          const newStates = { ...get().terminalLatestStates, ...states };
          set({ terminalLatestStates: newStates });
          try {
            localStorage.setItem(terminalStatesStoreKey, JSON.stringify(newStates));
          } catch {
            // Ignore storage errors
          }
        },
      },
    };
  })
);
export function useDirectSalesPaymentMethods() {
  const devicePM = usePaymentMethodsStore((s) => s.deviceIdPaymentMethod);
  const all = usePaymentMethodsStore((s) => s.directSales);

  return useMemo(() => {
    const integrated = all.filter((s) => s.provider !== 'UNINTEGRATED');
    const unintegrated = all.filter((s) => s.provider === 'UNINTEGRATED');
    return devicePM ? [devicePM, ...unintegrated] : [...integrated, ...unintegrated];
  }, [devicePM, all]);
}

export function useOpenBillPaymentMethods() {
  const devicePM = usePaymentMethodsStore((s) => s.deviceIdPaymentMethod);
  const all = usePaymentMethodsStore((s) => s.openBill);

  return useMemo(() => {
    const integrated = all.filter((s) => s.provider !== 'UNINTEGRATED');
    const unintegrated = all.filter((s) => s.provider === 'UNINTEGRATED');
    return devicePM ? [devicePM, ...unintegrated] : [...integrated, ...unintegrated];
  }, [devicePM, all]);
}
export const usePaymentMethodActions = () => usePaymentMethodsStore((state) => state.actions);

function mapLatestStateToDisplayState(latestState: TerminalState | undefined): TerminalDisplayState {
  switch (latestState) {
    case TerminalStateEnum.OK:
      return 'AVAILABLE';
    case TerminalStateEnum.BUSY:
      return 'BUSY';
    case TerminalStateEnum.UNREACHABLE:
    case TerminalStateEnum.UNKNOWN:
    case TerminalStateEnum.MAINTENANCE:
    case TerminalStateEnum.OUT_OF_ORDER:
      return 'OFFLINE';
    default:
      return 'AVAILABLE';
  }
}

export function useTerminalState(paymentMethodId: string): TerminalDisplayState {
  const { location } = useLocation();
  const latestState = usePaymentMethodsStore(
    useCallback((s) => s.terminalLatestStates[paymentMethodId], [paymentMethodId])
  );

  // Subscribe to active payments query for reactive updates
  const { data: activePayments } = useQueryActivePayments(location.locationId);

  const hasActivePayment = activePayments?.some(
    (ap) =>
      ap.paymentMethod.id === paymentMethodId &&
      ap.paymentState !== PaymentState.SUCCEEDED &&
      ap.paymentState !== PaymentState.FAILED
  );

  // BUSY if there's an active payment OR terminal reports BUSY
  if (hasActivePayment) {
    return 'BUSY';
  }

  return mapLatestStateToDisplayState(latestState);
}

const EMPTY_PAYMENT_METHODS: PaymentMethodDto[] = [];
export const useAvailablePaymentMethods = () =>
  usePaymentMethodsStore((state) => state.paymentMethods ?? EMPTY_PAYMENT_METHODS);

export function getPaymentMethodById(id: string): PaymentMethodDto | undefined {
  const methods = usePaymentMethodsStore.getState().paymentMethods;
  for (const method of methods) {
    if (method.id === id) {
      return method;
    }
  }
  return undefined;
}
